/**
 * A subject's documents: every file tagged with it. Its guides are written from
 * them and the assistant quotes them on questions about it. A file can be tagged
 * with several subjects, so removing it here only untags it from this one; the
 * file itself is deleted from its own page.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, IconButton, Skeleton } from '@servicecompressionllc/sc-design-system';
import { X } from '@servicecompressionllc/sc-design-system/icons';
import { useMe } from '../../hooks/useMe.js';
import { canWriteKnowledge } from '../../lib/knowledgeAccess.js';
import { humanSize } from '../../lib/format.js';
import { knowledgeApi, type KnowledgeDocument } from './api.js';

/** Kept in step with the API's ALLOWED_EXTENSIONS — .xls is absent on purpose
 *  (nothing readable comes out of the legacy binary format). */
export const ACCEPTED = '.pdf,.docx,.xlsx,.txt,.md,.csv,.tsv,.log';

export const SourceDocuments = ({ subjectId }: { subjectId: string }) => {
  const me = useMe();
  const canWrite = me.data ? canWriteKnowledge(me.data.user) : false;
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  // Deployment capability, not permission: without storage there is nowhere to
  // put a file, so the picker is disabled and says why rather than accepting a
  // file and failing on the way out.
  const capabilities = useQuery({
    queryKey: ['knowledge', 'capabilities'],
    queryFn: knowledgeApi.capabilities,
    staleTime: Infinity,
  });
  const storageReady = capabilities.data?.documents ?? true;

  const docs = useQuery({
    queryKey: ['knowledge', 'documents', subjectId],
    queryFn: () => knowledgeApi.documents(subjectId),
  });
  const buckets = useQuery({ queryKey: ['knowledge', 'buckets'], queryFn: knowledgeApi.buckets });
  const bucketIdOf = (id: string | null) =>
    id ?? (buckets.data ?? []).find((b) => b.isDefault)?.id ?? '';
  // The whole namespace: subject rows carry `documentCount`, and the delete
  // confirmation quotes it, so a stale count would misstate what's kept.
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['knowledge'] });

  const upload = useMutation({
    mutationFn: (file: File) => knowledgeApi.uploadDocument(subjectId, file),
    onSuccess: invalidate,
  });
  const untag = useMutation({
    mutationFn: (d: KnowledgeDocument) =>
      knowledgeApi.setDocumentSubjects(
        d.id,
        d.subjects.filter((s) => s.id !== subjectId).map((s) => s.id),
      ),
    onSuccess: () => {
      setRemoving(null);
      invalidate();
    },
  });

  if (docs.isLoading) return <Skeleton className="h-32" />;
  const rows = docs.data ?? [];

  return (
    <div className="rounded-lg border border-outline bg-surface-elevated p-4">
      <p className="text-sm font-medium text-ink">Documents</p>
      <p className="mt-0.5 text-xs text-ink-muted">
        Manuals and spec sheets about this equipment. Its guides are written from them, and the
        assistant quotes them on questions about it. With none, guides are written from general
        knowledge only and won’t state specific values.
      </p>

      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-ink-subtle">Nothing attached yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-outline">
          {rows.map((d) => {
            const others = d.subjects.filter((s) => s.id !== subjectId);
            return (
              <li key={d.id} className="flex items-center gap-3 py-2">
                <Link
                  to={`/knowledge/buckets/${bucketIdOf(d.bucketId)}/documents/${d.id}`}
                  className="min-w-0 flex-1 hover:underline"
                >
                  <span className="block truncate text-sm text-ink" title={d.filename}>
                    {d.filename}
                  </span>
                  <span className="block truncate text-xs text-ink-subtle">
                    {humanSize(d.sizeBytes)} · {d.uploadedByEmail}
                    {others.length > 0 && ` · also ${others.map((s) => s.name).join(', ')}`}
                  </span>
                </Link>
                {canWrite &&
                  (removing === d.id ? (
                    <span className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => untag.mutate(d)}
                        disabled={untag.isPending}
                      >
                        {untag.isPending ? 'Removing…' : 'Remove from this equipment'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRemoving(null)}>
                        Keep
                      </Button>
                    </span>
                  ) : (
                    <IconButton
                      aria-label={`Remove ${d.filename} from this equipment`}
                      variant="ghost"
                      onClick={() => setRemoving(d.id)}
                    >
                      <X size={14} />
                    </IconButton>
                  ))}
              </li>
            );
          })}
        </ul>
      )}

      {canWrite && (
        <div className="mt-3">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED}
            aria-label="Add a source document"
            disabled={!storageReady}
            title={
              storageReady
                ? undefined
                : 'Document storage isn’t set up on this deployment yet — ask Service Compression to enable it.'
            }
            className="block text-sm text-ink disabled:cursor-not-allowed disabled:opacity-50"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              if (inputRef.current) inputRef.current.value = '';
            }}
          />
          <p className="mt-1 text-xs text-ink-subtle">
            {storageReady
              ? 'PDF, Word, Excel, or text. Up to 50MB each. Stored in General Knowledge and tagged with this equipment.'
              : 'Document storage isn’t set up on this deployment yet.'}
          </p>
          {upload.isPending && <p className="mt-2 text-sm text-ink-muted">Uploading…</p>}
          {upload.error && (
            <Banner variant="danger" className="mt-2">
              <Banner.Description>{(upload.error as Error).message}</Banner.Description>
            </Banner>
          )}
          {untag.error && (
            <Banner variant="danger" className="mt-2">
              <Banner.Description>{(untag.error as Error).message}</Banner.Description>
            </Banner>
          )}
        </div>
      )}
    </div>
  );
};
